/**
 *
 * ProductCard
 *
 */
import * as React from 'react';
import styled from 'styled-components/macro';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheckCircle, faCheck, faXmarkCircle} from "@fortawesome/pro-solid-svg-icons";

interface Feature {
  text: string;
  isPositive: boolean;
}

interface Props {
  title: string,
  description: string,
  features: Feature[],
  onSelect?: () => void,
  priceAmount?: string,
  priceFrequency: string,
  submitText?: string,
  isCompact: boolean,
  isCurrent: boolean,
  isSubmitting?: boolean,
  isYearly: boolean,
  height: string,
  creditConversions?: [string],
  checkboxStyle: boolean,
  isChecked: boolean,
  onCheckboxChange: () => void,
  showFreePlan?: boolean,
  showContactButton?: boolean,
  onContactClick?: () => void,
  isPopular?: boolean,
}

export function ProductCard(props: Props) {
  const {
    title,
    description,
    features,
    onSelect,
    priceAmount,
    priceFrequency,
    submitText,
    isCompact,
    isCurrent,
    isSubmitting,
    isYearly,
    height,
    checkboxStyle,
    isChecked,
    onCheckboxChange,
    isPopular,
  } = props

  let wrapperStyle = {}
  if (height) Object.assign(wrapperStyle, { height })

  return  (
      <Container compact={isCompact} checkboxStyle={checkboxStyle} isChecked={isChecked} isPopular={isPopular}>
        {isPopular && <PopularBand>Best Value</PopularBand>}
        <div className={isCompact ? "pricing-card-wrapper compact" : "pricing-card-wrapper"} onClick={checkboxStyle ? onCheckboxChange : undefined}>
          <div className={isCompact ? "pricing-card compact" : "pricing-card"}>
            <div>
            <div className="checkbox-wrapper">
              <h1 className={isCompact ? "title compact" : "title"}>
                {title}
              </h1>
              {checkboxStyle && isChecked && (
                <CheckIconWrapper>
                  <FontAwesomeIcon icon={faCheck} style={{color: "#FF74E9"}}/>
                </CheckIconWrapper>
              )}
            </div>

              <div className="price-action">
                {priceAmount && (
                  <div className="pricing">
                    <span className="amount">{priceAmount} </span>
                    <span className="frequency">{priceFrequency}</span>
                    {isYearly && (
                      <div className="billed-yearly"> Billed Yearly </div>
                    )}
                  </div>
                )}
                
                {/* Button/Current Plan Display Logic */}
                {(() => {
                  // Don't show anything for in checkbox style
                  if (checkboxStyle) {
                    // Show "CURRENT PLAN" when it's the current plan
                    if (isCurrent) {
                      return <CurrentPlanText>CURRENT PLAN</CurrentPlanText>;
                    }
                    return null;
                  }
                  
                  // Show "CURRENT PLAN" when it's the current plan
                  if (isCurrent) {
                    return <div className="current-plan-text">CURRENT PLAN</div>;
                  }
                  
                  // Show button only if onSelect and submitText are provided
                  if (onSelect && submitText) {
                    return (
                      <button 
                        className="primary-button" 
                        onClick={isSubmitting ? () => {} : onSelect}
                      >
                        <div className="button-label">
                          {isSubmitting ? "REDIRECTING..." : submitText}
                        </div>
                      </button>
                    );
                  }
                  return null;
                })()}
              </div>

              <div className="list-of-items">
                {false && description && (
                  <p className="text-wrapper">{description}</p>
                )}
                {features.map((feature, index) => (
                    <div className="list-value-prop" key={index}>
                      <FontAwesomeIcon 
                        icon={feature.isPositive ? faCheckCircle : faXmarkCircle} 
                        color={feature.isPositive ? "#55CDC4" : "#FC5555"}
                      />
                      <p className={isCompact ? "value-prop compact" : "value-prop"}>{feature.text}</p>
                    </div>
                ))}
              </div>

              {props.creditConversions && (
                <CreditConversionList>
                  {props.creditConversions.map(c => <span>{c}<br/></span>)}
                </CreditConversionList>
              )}

              {props.showContactButton && (
                <ContactButton onClick={props.onContactClick}>
                  Contact Us
                </ContactButton>
              )}
            </div>

              
          </div>
        </div>
      </Container>
  );
}
const Container = styled.div<{ compact?: boolean; checkboxStyle?: boolean; isChecked?: boolean; isPopular?: boolean }>`
  background-color: transparent;
  width: ${props => props.compact ? '350px' : 'auto'};
  height: 'auto';
  margin-bottom: 20px;
  align-items: center;
  position: relative;
  padding: 5px;
  overflow: hidden;
  @media (max-width: 600px) {
    padding: 0;
    overflow: visible;
  }

  .pricing-card-wrapper {
    border: 0;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 380px;
    &.compact {
      width: 100%;
      height: auto;
      padding: 0;
    }
    

    @media only screen and (max-width: 600px) {
      padding: 0;
      overflow: visible;
    }
  }

  .pricing-card {
    height: 560px;
    overflow: hidden;
    width: 98%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border-radius: 20px;
    border: 1px solid ${props => props.isPopular ? '#FF74E9' : 'var(--secondary-dark, #7B1FA2)'};
    background: #1D0D2C;

    @media only screen and (max-width: 600px) {
      width: 90%;
      height: auto;
      padding-bottom: 10%;
      overflow: visible;
    }

    &.compact {
      width: 100%;
      height: 490px;
    }

    ${props => props.checkboxStyle && `
      cursor: pointer;
      transition: border-color 0.3s, border-width 0.3s, background 0.3s;
      max-height: 420px;
      
      &:hover {
        background: linear-gradient(0deg, rgba(245, 225, 255, 0.04) 0%, rgba(245, 225, 255, 0.04) 100%), #1D0D2C;
      }
    `}

    ${props => props.checkboxStyle && props.isChecked && `
      border-color: #FF74E9;
      border-width: 3px;
      background: linear-gradient(0deg, rgba(245, 225, 255, 0.08) 0%, rgba(245, 225, 255, 0.08) 100%), #1D0D2C;
      box-shadow: 0 0 15px rgba(186, 156, 255, 0.3);
    `}
  }

  .title {
    white-space: nowrap;
    text-align: left;
    margin: 37px 33px 0;
    color: #BA9CFF;
    font-family: Raleway;
    font-size: 36px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: -0.72px;
    
    &.compact {
      font-size: 24px;
    }
  }

  .list-of-items {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 18px;
    margin: 0 40px;
    padding-top: 20px;
  }

  .text-wrapper {
    color: #FFF;
    font-family: Jura;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: -0.32px;
    text-transform: uppercase;
    margin-top: 0;
  }

  .list-value-prop {
    align-items: flex-start;
    display: flex;
    font-size: 18px;
    gap: 8px;
  }

  .value-prop {
    color: #f5e1febd;
    font-family: "Raleway";
    font-size: 18px;
    font-weight: 500;
    letter-spacing: 0;
    line-height: normal;
    margin: -2px 0 0 10px;
    
    &.compact {
      font-size: 14px;
    }
  }

  .primary-button {
    justify-self: flex-end;
    font-family: "Jura";
    align-self: stretch !important;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin: 0 40px;
  }
  

  .current-plan-text {
    color: rgba(245, 225, 255, 0.6);
    font-family: "Jura";
    font-size: 16px;
    font-weight: 600;
    left: 0;
    letter-spacing: 0;
    line-height: 40px;
    text-align: center;
    white-space: nowrap;
    border: 2px solid #645270;
    margin: 15px 40px 0;
    border-radius: 15px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
  }

  .pricing {
    color: #ffffff;
    font-family: "Jura";
    font-size: 32px;
    font-weight: 400;
    letter-spacing: 0;
    line-height: 1em;
    white-space: nowrap;
    margin: 10px 40px 0;

    .amount {
      font-weight: 700;
      margin-right: -5px;
    }

    .frequency {
      font-family: "Jura";
      font-weight: 300;
      font-size: 18px;
    }
    
    .billed-yearly {
      font-family: "Jura";
      font-weight: 300;
      font-size: 16px;
      line-height: 1em;
      font-style: italic;
    }
  }
  
  .use-same-email-notice {
    color: #ffffff99;
    font-family: "Raleway";
    font-weight: 300;
    font-size: 18px;
    letter-spacing: 0;
    line-height: 20px;
    margin: 0 40px;
  }
  
  .price-action {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    gap: 10px;
  }
  
  .checkbox-wrapper {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    font-size: 0.8em;
    gap: 1.5em;

    @media (max-width: 600px) {
      gap: 1em;
    }
  }
`;

const CreditConversionList = styled.div`
  color: var(--primary-main, #FF74E9);
  font-family: Raleway;
  font-size: 14px;
  font-style: italic;
  font-weight: 500;
  line-height: 120%;
  padding: 20px 20px 20px 40px;
`;

const CheckIconWrapper = styled.div`
  position: absolute;
  top: 40px;
  right: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

const CurrentPlanText = styled.div`
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  color: #FF74E9;
  font-family: Raleway;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
`;

const ContactButton = styled.button`
  margin: 20px 40px;
  padding: 20px 24px;
  background: transparent;
  border: 1px solid #FF74E9;
  color: #FF74E9;
  font-family: "Jura";
  font-size: 24px;
  font-weight: 500;
  border-radius: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 80%;
  align-self: center;
  &:hover {
    background: rgba(255, 116, 233, 0.1);
  }
`;

const PopularBand = styled.div`
  position: absolute;
  top: 30px;
  right: -36px;
  background: #FF74E9;
  color: #1D0D2C;
  padding: 5px 40px;
  transform: rotate(45deg);
  font-family: "Jura";
  font-size: 14px;
  font-weight: 1000;
  text-transform: uppercase;
  z-index: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  @media (max-width: 600px) {
    top: 0px;
    right: 0px;
    border-radius: 0px 10px 0px 10px;
    transform: rotate(0deg);
  }
`;